import { useEffect, useState } from "react";

/**
 * The unit hover intel shows (`projects/hover-intel.md` 1.4): the stage's hovered token, at once
 * (no dwell: the developer wants intel with no delay), cleared the moment it leaves.
 *
 * `hoverId` is `null` while `off` and while a pointer button is held (a token drag or a pan).
 * `off` covers `suppressed` (the caller's armed aim, hovered menu row, direction prompt or EDIT
 * mode) and a touch press until the mouse moves again: a finger has no hover, and its tap selects.
 * `off` also turns off the selection's zones (`projects/intel-follows-selection.md`), which a
 * held button does not: a dragged unit's rings follow the drag.
 */
export function useHoverIntelUnit({ hoveredId, suppressed }: { hoveredId: string | null; suppressed: boolean }): { hoverId: string | null; off: boolean } {
  const [pointer, setPointer] = useState({ down: false, touch: false });
  useEffect(() => {
    // Only a real change sets state: CombatApp re-renders on each one.
    const set = (down: boolean, touch: boolean) => setPointer((p) => (p.down === down && p.touch === touch ? p : { down, touch }));
    const onDown = (e: PointerEvent) => set(true, e.pointerType === "touch");
    const onUp = (e: PointerEvent) => set(false, e.pointerType === "touch");
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "mouse") setPointer((p) => (p.touch ? { ...p, touch: false } : p));
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("pointerup", onUp, true);
    window.addEventListener("pointercancel", onUp, true);
    window.addEventListener("pointermove", onMove, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("pointerup", onUp, true);
      window.removeEventListener("pointercancel", onUp, true);
      window.removeEventListener("pointermove", onMove, true);
    };
  }, []);

  const off = suppressed || pointer.touch;
  const candidate = off || pointer.down ? null : hoveredId;
  return { hoverId: candidate, off };
}
